import { ref, computed } from 'vue'

export interface Rectangle {
  startX: number
  startY: number
  endX: number
  endY: number
}

export interface Point {
  x: number // screen coordinates
  y: number // screen coordinates
}

export function useRectangleSelection() {
  const isSelecting = ref(false)
  const selectionRect = ref<Rectangle | null>(null)
  const startPoint = ref<Point | null>(null)

  /**
   * Computed rectangle with normalized coordinates (top-left to bottom-right)
   */
  const normalizedRect = computed(() => {
    if (!selectionRect.value) return null

    const rect = selectionRect.value
    return {
      left: Math.min(rect.startX, rect.endX),
      top: Math.min(rect.startY, rect.endY),
      right: Math.max(rect.startX, rect.endX),
      bottom: Math.max(rect.startY, rect.endY),
      width: Math.abs(rect.endX - rect.startX),
      height: Math.abs(rect.endY - rect.startY)
    }
  })

  /**
   * Start rectangle selection
   */
  const startSelection = (x: number, y: number) => {
    isSelecting.value = true
    startPoint.value = { x, y }
    selectionRect.value = {
      startX: x,
      startY: y,
      endX: x,
      endY: y
    }
  }

  /**
   * Update rectangle during drag
   */
  const updateSelection = (x: number, y: number) => {
    if (!isSelecting.value || !selectionRect.value) return

    selectionRect.value = {
      ...selectionRect.value,
      endX: x,
      endY: y
    }
  }

  /**
   * End rectangle selection
   */
  const endSelection = () => {
    isSelecting.value = false
    selectionRect.value = null
    startPoint.value = null
  }

  /**
   * Check if a point (in screen coordinates) is inside the selection rectangle
   */
  const isPointInSelection = (x: number, y: number): boolean => {
    if (!normalizedRect.value) return false

    const rect = normalizedRect.value
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  /**
   * Cancel selection (e.g., on Escape key)
   */
  const cancelSelection = () => {
    isSelecting.value = false
    selectionRect.value = null
    startPoint.value = null
  }

  return {
    isSelecting,
    selectionRect,
    normalizedRect,
    startSelection,
    updateSelection,
    endSelection,
    isPointInSelection,
    cancelSelection
  }
}
